import { formatStaffName } from "@/lib/format-data";

import type {
  DateRangeFields,
  GroupPaginationState,
  PaginationState,
  SortDirectionState,
  TimezoneField,
} from "./shared-types";
import {
  type FilterDefinition,
  type FilterKey,
  type FilterOption,
  type FilterQueryMetadata,
  type UrlPolicy,
  booleanFilter,
  dateRangeFilter,
  entitySetFilter,
  enumFilter,
  numberFilter,
  textFilter,
} from "@/features/table-filters/contract";

type EntityArgs<Request extends object> = {
  presented?: boolean;
  control?: "multiSelect" | "location";
  queryKey?: string;
  query?: FilterQueryMetadata<Request> | false;
  urlPolicy?: UrlPolicy;
};

export function analyticsDateRange<
  Request extends object,
  Start extends FilterKey<Request>,
  End extends FilterKey<Request>,
>(args: {
  startKey: Start;
  endKey: End;
  label?: string;
  presented?: boolean;
  urlPolicy?: UrlPolicy;
  startQueryKey?: string;
  endQueryKey?: string;
}): FilterDefinition<Request> {
  return dateRangeFilter<Request>({
    label: "Date Range",
    presented: false,
    ...args,
  });
}

export function standardDateRange<Request extends DateRangeFields>(
  overrides: Omit<
    Parameters<
      typeof analyticsDateRange<Request, FilterKey<Request>, FilterKey<Request>>
    >[0],
    "startKey" | "endKey"
  > = {},
): FilterDefinition<Request> {
  return analyticsDateRange<Request, FilterKey<Request>, FilterKey<Request>>({
    startKey: "startDate" as FilterKey<Request>,
    endKey: "endDate" as FilterKey<Request>,
    ...overrides,
  });
}

export function hiddenTimezone<
  Request extends TimezoneField,
>(): FilterDefinition<Request> {
  return hiddenText<Request, FilterKey<Request>>(
    "timezone" as FilterKey<Request>,
    "Timezone",
  );
}

export function entityIds<
  Request extends object,
  Key extends FilterKey<Request>,
>({
  key,
  label,
  options = [],
  ...args
}: EntityArgs<Request> & {
  key: Key;
  label: string;
  options?: readonly FilterOption[];
}): FilterDefinition<Request> {
  return entitySetFilter<Request>({ key, label, options, ...args });
}

function canonicalEntity<Key extends string>(key: Key, label: string) {
  return <Request extends Partial<Record<Key, string[]>>>(
    options: readonly FilterOption[] = [],
    args: EntityArgs<Request> = {},
  ) =>
    entityIds<Request, FilterKey<Request>>({
      key: key as unknown as FilterKey<Request>,
      label,
      options,
      ...args,
    });
}

/**
 * Provider choices split into Active and Archived groups, with Archived
 * collapsed. Ungrouped when no provider is archived.
 */
export function providerOptions(
  providers: readonly {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    isArchived?: boolean | null;
  }[],
): FilterOption[] {
  const hasArchived = providers.some((provider) => provider.isArchived);
  const toOption = (provider: (typeof providers)[number]): FilterOption => ({
    label: formatStaffName(provider),
    value: provider.id,
    ...(hasArchived && {
      group: provider.isArchived ? "Archived" : "Active",
      groupCollapsed: Boolean(provider.isArchived),
    }),
  });
  return [
    ...providers.filter((provider) => !provider.isArchived),
    ...providers.filter((provider) => provider.isArchived),
  ].map(toOption);
}

export const locationIds = canonicalEntity("locationIds", "Locations");
export const providerIds = canonicalEntity("providerIds", "Providers");
export const serviceIds = canonicalEntity("serviceIds", "Services");
export const patientIds = canonicalEntity("patientIds", "Patients");
export const promotionIds = canonicalEntity("promotionIds", "Promotion");

export function enumOptions<const Values extends readonly string[]>(
  values: Values,
  labels: Partial<Record<Values[number], string>> = {},
): readonly FilterOption<Values[number]>[] {
  return values.map((value: Values[number]) => ({
    value,
    label: labels[value] ?? value,
  }));
}

const SORT_DIRECTION_OPTIONS = enumOptions(["asc", "desc"] as const, {
  asc: "Ascending",
  desc: "Descending",
});

export const sortDirectionOptions = () => SORT_DIRECTION_OPTIONS;

export function hiddenSortDirection<
  Request extends SortDirectionState,
>(): FilterDefinition<Request> {
  return enumFilter<Request>({
    key: "sortDirection" as FilterKey<Request>,
    label: "Sort Direction",
    control: "none",
    presented: false,
    urlPolicy: "none",
    options: SORT_DIRECTION_OPTIONS,
  });
}

type HiddenKind = "text" | "number" | "boolean";

function hiddenScalar<Request extends object>(
  kind: HiddenKind,
  key: FilterKey<Request>,
  label: string,
): FilterDefinition<Request> {
  const args = { key, label, presented: false, urlPolicy: "none" } as const;
  if (kind === "number") return numberFilter<Request>(args);
  if (kind === "boolean") return booleanFilter<Request>(args);
  return textFilter<Request>({ ...args, control: "none" });
}

export const hiddenText = <
  Request extends object,
  Key extends FilterKey<Request>,
>(
  key: Key,
  label = String(key),
) => hiddenScalar<Request>("text", key, label);

export const hiddenNumber = <
  Request extends object,
  Key extends FilterKey<Request>,
>(
  key: Key,
  label = String(key),
) => hiddenScalar<Request>("number", key, label);

export const hiddenBoolean = <
  Request extends object,
  Key extends FilterKey<Request>,
>(
  key: Key,
  label = String(key),
) => hiddenScalar<Request>("boolean", key, label);

function hiddenFields<Request extends object, Key extends FilterKey<Request>>(
  kind: HiddenKind,
  keys: readonly Key[],
): readonly FilterDefinition<Request>[] {
  return keys.map((key) => hiddenScalar<Request>(kind, key, String(key)));
}

export const hiddenTexts = <
  Request extends object,
  Key extends FilterKey<Request>,
>(
  keys: readonly Key[],
) => hiddenFields<Request, Key>("text", keys);

export const hiddenNumbers = <
  Request extends object,
  Key extends FilterKey<Request>,
>(
  keys: readonly Key[],
) => hiddenFields<Request, Key>("number", keys);

export function hiddenGroupPagination<
  Request extends GroupPaginationState,
>(): readonly FilterDefinition<Request>[] {
  return [
    ...hiddenNumbers<Request, FilterKey<Request>>([
      "pageSize" as FilterKey<Request>,
      "page" as FilterKey<Request>,
    ]),
    hiddenText<Request, FilterKey<Request>>("groupKey" as FilterKey<Request>),
  ];
}

export function hiddenPagination<
  Request extends PaginationState,
>(): readonly FilterDefinition<Request>[] {
  return hiddenNumbers<Request, FilterKey<Request>>([
    "page" as FilterKey<Request>,
    "perPage" as FilterKey<Request>,
  ]);
}

export function singleArrayItemQuery<
  Request extends object,
  Key extends FilterKey<Request>,
>(key: Key, queryKey: string): FilterQueryMetadata<Request> {
  return {
    keys: [queryKey],
    serialize: ({ params, request }) => {
      const value = request[key];
      if (Array.isArray(value) && value[0] !== undefined) {
        params.append(queryKey, String(value[0]));
      }
    },
  };
}

export function repeatedTextQuery<
  Request extends object,
  Key extends FilterKey<Request>,
>(key: Key, queryKey: string): FilterQueryMetadata<Request> {
  return {
    keys: [queryKey],
    serialize: ({ params, request }) => {
      const value = request[key];
      const values = Array.isArray(value) ? value : value ? [value] : [];
      values.forEach((item) => {
        if (item) params.append(queryKey, String(item));
      });
    },
  };
}
